import { object } from '@calendly/form/validation'
import { I18n } from '@calendly/i18n'
import { omit, pick, uniq } from 'lodash-es'

import { getAvailabilityValidationSchema, getFormatAvailabilityValues } from '../components/availability'
import {
  getEventDetailsInitialValues,
  getEventDetailsValidationSchema,
  getFormatEventDetailsValues
} from '../components/event_details'
import { filterEnabledMemberships } from '../components/helpers'
import { getFormatLocationValues, getLocationValidationSchema } from '../components/location'
import { getFormatLockingRulesValues, getSectionLockInitialValues } from '../components/locking_rule_switch'
import { mergeArraysOfObjectsByKeys } from '../utils'

export const getInitialValues = ({ eventType, defaultAvailabilityRules }) => {
  const eventDetailsAttributes = getEventDetailsInitialValues({
    eventType, defaultAvailabilityRules, isCreation: true
  })

  return {
    ...eventDetailsAttributes,
    ...getSectionLockInitialValues(eventType),
    name: I18n.t('publisher.event_types.create.default_name')
  }
}

const NOT_FOR_BACKEND_MEMBERSHIP_ATTRIBUTES = [
  'active_availability_rule_id', 'active_availability_rule_name',
  'is_gotomeeting_configured', 'is_microsoft_teams_configured', 'is_webex_configured', 'is_zoom_configured',
  'google_conferencing_configured', 'has_active_notifiable_calendar',
  'avatar_letter', 'avatar_url', 'email'
]

const getLocationOrganizerId = (poolingType, userPools, enabledHosts, memberships) => {
  const hasCollectiveUserPool = poolingType === 'multi_pool' && userPools.some((pool) => pool.pooling_type === 'collective')

  if (hasCollectiveUserPool) {
    const userPoolUID = userPools.find((pool) => pool.pooling_type === 'collective').key
    const poolEnabledHosts = enabledHosts.filter((host) => host.user_pool_uid === userPoolUID)
    const poolMemberships = memberships.filter((host) => host.user_pool_uid === userPoolUID)

    return poolEnabledHosts.find((membership) => membership.location_organizer)?.user_id || poolMemberships[0]?.user_id
  }

  return enabledHosts.find((membership) => membership.location_organizer)?.user_id || memberships[0]?.user_id
}

export const getFormatValuesToServerData = ({ eventType }) => (values) => {
  const formatEventDetailsValues = getFormatEventDetailsValues({ eventType, isCreation: true })
  const formatAvailabilityValues = getFormatAvailabilityValues(eventType)
  const formatLocationValues = getFormatLocationValues(eventType)
  const formatLockingRulesValues = getFormatLockingRulesValues(eventType)

  const {
    event_type_memberships_attributes: eventDetailsMembershipsAttributes = [],
    ...eventDetailsAttributes
  } = formatEventDetailsValues(values)
  const {
    event_type_memberships_attributes: availabilityAttributesMembershipsAttributes = [],
    ...availabilityAttributes
  } = formatAvailabilityValues(values)
  const {
    event_type_memberships_attributes: locationMembershipsAttributes = [],
    ...locationAttributes
  } = formatLocationValues(values)

  const eventTypeMembershipsAttributes = eventType.isMETParent ? {} : {
    event_type_memberships_attributes: mergeArraysOfObjectsByKeys([
      eventDetailsMembershipsAttributes,
      availabilityAttributesMembershipsAttributes,
      locationMembershipsAttributes,
    ], 'id', 'user_id', 'organization_invitation_id').map(
      (m) => omit(m, NOT_FOR_BACKEND_MEMBERSHIP_ATTRIBUTES)
    )
  }

  const baseAttributes = {
    ...eventDetailsAttributes,
    ...availabilityAttributes,
    ...locationAttributes,
    ...eventTypeMembershipsAttributes,
    ...formatLockingRulesValues(values),
    origin: 'event_type_editor',
    kind: eventType.kind,
    activate: true,
  }

  if (!eventType.isMultiHost && !eventType.isTeam) return baseAttributes

  const { memberships } = values
  let poolingType = eventType.pooling_type
  let userPools = values.userPools

  if (poolingType === 'multi_pool') {
    const notEmptyUserPoolUids = uniq(memberships.map((membership) => membership.user_pool_uid))

    if (notEmptyUserPoolUids.length === 1) {
      const userPool = userPools.find(({ uuid, key }) => (uuid || key) === notEmptyUserPoolUids[0])

      poolingType = userPool.pooling_type
    } else {
      userPools = userPools.filter(({ uuid, key }) => notEmptyUserPoolUids.includes(uuid || key))
    }
  }

  const enabledHosts = filterEnabledMemberships(memberships)
  const locationKind = values.location_configurations?.[0]?.kind
  const hasAutoOrganizer = ['google_conference', 'microsoft_teams_conference'].includes(locationKind)
  const locationOrganizerId = getLocationOrganizerId(poolingType, userPools, enabledHosts, memberships)

  return {
    ...baseAttributes,
    ...pick(eventType, 'locale', 'type'),
    owner_uuid: eventType.isTeam ? null : eventType.owner_uuid,
    location_organizer_id: hasAutoOrganizer ? null : locationOrganizerId,
  }
}

export const getValidationSchema = ({ eventType }) => object({})
  .concat(getEventDetailsValidationSchema({ eventType, isCreation: true }))
  .concat(getAvailabilityValidationSchema())
  .concat(getLocationValidationSchema(eventType))
